import { useState } from 'react';
import { useQuery } from 'react-query';
import { inventarioService, Existencia } from '../../services/inventarioService';
import { sucursalService } from '../../services/sucursalService';
import { useAuthStore } from '../../store/authStore';
import AjusteInventarioForm from '../../components/Inventario/AjusteInventarioForm';

const InventarioList = () => {
  const [sucursalFilter, setSucursalFilter] = useState<number | undefined>(undefined);
  const [lowStockFilter, setLowStockFilter] = useState(false);
  const [showAjusteForm, setShowAjusteForm] = useState(false);
  const [selectedExistencia, setSelectedExistencia] = useState<Existencia | null>(null);
  const { user } = useAuthStore();

  const { data: sucursalesData } = useQuery(
    'sucursales',
    () => sucursalService.getAll(1, 100)
  );

  const { data: existencias, isLoading } = useQuery(
    ['existencias', sucursalFilter, lowStockFilter],
    () => inventarioService.getExistencias(sucursalFilter, undefined, lowStockFilter)
  );

  const handleAjustar = (existencia: Existencia) => {
    setSelectedExistencia(existencia);
    setShowAjusteForm(true);
  };

  const handleCloseForm = () => {
    setShowAjusteForm(false);
    setSelectedExistencia(null);
  };

  if (isLoading) {
    return <div className="text-center py-8">Cargando...</div>;
  }

  const getStockStatus = (existencia: Existencia) => {
    if (existencia.cantidad <= 0) {
      return { text: 'Sin Stock', color: 'bg-red-100 text-red-800' };
    }
    if (existencia.cantidad <= existencia.cantidadMinima) {
      return { text: 'Bajo Stock', color: 'bg-yellow-100 text-yellow-800' };
    }
    if (existencia.cantidadMaxima && existencia.cantidad >= existencia.cantidadMaxima) {
      return { text: 'Sobre Stock', color: 'bg-blue-100 text-blue-800' };
    }
    return { text: 'Normal', color: 'bg-green-100 text-green-800' };
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Inventario</h1>
        <button
          onClick={() => setShowAjusteForm(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition"
        >
          + Ajuste de Inventario
        </button>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b space-y-4">
          <div className="flex space-x-4">
            <select
              value={sucursalFilter || ''}
              onChange={(e) => setSucursalFilter(e.target.value ? Number(e.target.value) : undefined)}
              className="px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Todas las sucursales</option>
              {sucursalesData?.data.sucursales.map((sucursal) => (
                <option key={sucursal.id} value={sucursal.id}>
                  {sucursal.name}
                </option>
              ))}
            </select>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={lowStockFilter}
                onChange={(e) => setLowStockFilter(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm font-medium text-gray-700">Solo bajo stock</span>
            </label>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sucursal</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cantidad</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mín</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Máx</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Costo Promedio</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {existencias?.map((existencia) => {
                const stockStatus = getStockStatus(existencia);
                return (
                  <tr key={existencia.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {existencia.producto?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {existencia.producto?.sku || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {existencia.sucursal?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {(Number(existencia.cantidad) || 0).toFixed(3)} {existencia.producto?.unidadMedida || ''}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {(Number(existencia.cantidadMinima) || 0).toFixed(3)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {existencia.cantidadMaxima ? (Number(existencia.cantidadMaxima) || 0).toFixed(3) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${existencia.costoPromedio ? (Number(existencia.costoPromedio) || 0).toFixed(2) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${stockStatus.color}`}>
                        {stockStatus.text}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleAjustar(existencia)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        Ajustar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {existencias && existencias.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No hay existencias registradas
          </div>
        )}
      </div>

      {showAjusteForm && (
        <AjusteInventarioForm
          existencia={selectedExistencia}
          onClose={handleCloseForm}
        />
      )}
    </div>
  );
};

export default InventarioList;

